/**
 * A pack's cover in the catalogue grid, drawn as the top sheet of a small
 * pile: two sheets peek out behind it, up and to the right. Now that packs
 * get a group portrait of their heroes, a pack card and a story card both
 * show children — the pile is what says "several stories" at a glance.
 *
 * The sheets stay inside the component's box: it reserves a 10px gutter on
 * the top and right edges and the cover shrinks by that much, so nothing
 * spills into the neighbouring grid cell (2 columns on a phone). Offsets
 * are small enough (≤5px translate, ≤2.5° rotation — a corner of a rotated
 * square moves ~3px) to fit that gutter with room to spare.
 *
 * Sheets reuse the cover's frame (8px corners, `border-line` hairline) and
 * the pack's colorway, muted into `--color-surface`, so they read as more
 * pages of the same pack rather than as decoration. The back sheet is the
 * dimmer of the two, which gives the pile its depth without any shadow.
 *
 * Grid only: the pack page shows the pack's stories right below, so there
 * the plain `<Cover>` is used.
 */
import type { ComponentProps, CSSProperties, ReactNode } from "react";
import { Cover } from "./Cover";
import { resolveColorway } from "../lib/colorways";

type CoverProps = ComponentProps<typeof Cover>;

/** Back to front: how far each sheet is pushed and turned, and how much of
 *  the colorway's dark accent it carries. */
const SHEETS = [
  { transform: "translate(5px, -5px) rotate(2.5deg)", tint: 22 },
  { transform: "translate(2.5px, -2.5px) rotate(1.2deg)", tint: 38 },
];

export function CoverStack({ children, ...cover }: CoverProps & {
  /** Overlays (badges) positioned against the top cover, not the pile. */
  children?: ReactNode;
}) {
  const rounded = cover.rounded ?? "rounded-[8px]";
  const cw = resolveColorway(cover.colorway, cover.colorKey || cover.title);
  return (
    <div className="pt-2.5 pr-2.5" data-cover-stack>
      <div className="relative">
        {SHEETS.map((s, i) => (
          <div
            key={i}
            aria-hidden
            className={`absolute inset-0 ${rounded} border border-line`}
            style={{
              transform: s.transform,
              background: `color-mix(in srgb, ${cw.accentDark} ${s.tint}%, var(--color-surface))`,
            } as CSSProperties}
          />
        ))}
        <Cover {...cover} className={`relative ${cover.className ?? ""}`} />
        {children}
      </div>
    </div>
  );
}
